/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import { default as fragments } from '../../fragments'
import 'react-datepicker/dist/react-datepicker.css'

import {
  Input,
  Spinner,
  InfoDisplay,
  RadioGroup,
  SelectWithData,
  SpanWithData,
  SvgIcon
} from '../'

import {
  getDateUTC,
  getDateOfBirthFromSouthAfricanID,
  isValidSouthAfricanID
} from '../../utils'

class IdentityInformation extends React.PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    readOnly: PropTypes.bool,
    keyColumnSize: PropTypes.string
  }

  static defaultProps = {
    readOnly: false,
    keyColumnSize: '1-of-4'
  }

  constructor(props) {
    super(props)
    this.state = {
      editPersonalInfo: false,
      chooserOpen: '',
      firstName: "Loading",
      lastName: "Loading",
      otherName: "Loading",
      language: "Loading",
      dateOfBirth: null,
      deceased: false,
      membershipType: "Loading",
      membershipStatus: "Loading",
      earnbacksType: "Loading",
      earnbacksStatus: "Loading",
      productsStatus: "Loading",
      identityType: "Loading",
      identityValue: "Loading",
      saveInProgress: false
    }
  }

  goEditPersonalInfo = () => {
    let identity = this.props.data.identity
    let dobMoment = identity.dateOfBirth
      ? getDateUTC(moment(identity.dateOfBirth))
      : null

    this.setState({
      editPersonalInfo: true,
      firstName: identity.firstName,
      lastName: identity.lastName,
      otherName: identity.otherName,
      language: identity.language,
      dateOfBirth: dobMoment,
      deceased: identity.deceased,
      membershipType: identity.membershipType,
      membershipStatus: identity.membershipStatus,
      earnbacksType: identity.earnbacksType,
      earnbacksStatus: identity.earnbacksStatus,
      productsStatus: identity.productsStatus,
      identityType: identity.identityType,
      identityValue: identity.identityValue
    })
  }

  goReadOnly = () => {
    this.setState({
      editPersonalInfo: false,
      saveInProgress: false
    })
  }

  noChangesToSave = () => {
    let identity = this.props.data.identity

    return identity.firstName === this.state.firstName &&
      identity.lastName === this.state.lastName &&
      identity.otherName === this.state.otherName &&
      identity.language === this.state.language &&
      (
        (identity.dateOfBirth === null && this.state.dateOfBirth === null) ||
        (this.state.dateOfBirth && identity.dateOfBirth === this.state.dateOfBirth.format("YYYY-MM-DD"))
      ) &&
      identity.deceased === this.state.deceased &&
      identity.identityType === this.state.identityType &&
      identity.identityValue === this.state.identityValue
  }

  handlePersonalInfoUpdate = () => {
    this.setState({ saveInProgress: true })
    let identity = this.props.data.identity
    let personalInfo = {
      id: this.props.data.identity.id
    }

    if (identity.firstName !== this.state.firstName)
      personalInfo.firstName = this.state.firstName

    if (identity.lastName !== this.state.lastName)
      personalInfo.lastName = this.state.lastName

    if (identity.otherName !== this.state.otherName)
      personalInfo.otherName = this.state.otherName

    if (identity.language !== this.state.language)
      personalInfo.language = this.state.language
        ? this.state.language.toLowerCase() // necessary when lang field wasn't edited
        : this.state.language

    if (this.state.dateOfBirth && identity.dateOfBirth !== this.state.dateOfBirth.format("YYYY-MM-DD")
      && this.state.dateOfBirth.isValid())
      personalInfo.dateOfBirth = this.state.dateOfBirth.format("YYYY-MM-DD")

    if (identity.deceased !== this.state.deceased)
      personalInfo.deceased = this.state.deceased

    if (identity.identityType !== this.state.identityType)
      personalInfo.identityType = this.state.identityType
        ? this.state.identityType.toLowerCase()
        : this.state.identityType

    if (identity.identityValue !== this.state.identityValue)
      personalInfo.identityValue = this.state.identityValue

    if (Object.keys(personalInfo).length > 1) {   // check whether fields other than just 'id' exist on obj
      this.props.updateIdentity({ variables: { input: personalInfo }})
        .then(this.goReadOnly)
        .catch(() => this.setState({ saveInProgress: false }))
    }
  }

  handleIdentityNumberInput = (e) => {
    let idNumber = e.target.value

    if (this.state.identityType === "za_id" && isValidSouthAfricanID(idNumber, moment)) {
      let dob = getDateOfBirthFromSouthAfricanID(idNumber, moment)

      this.setState({
        identityValue: idNumber,
        dateOfBirth: dob
      })
    } else {
      this.setState({ identityValue: idNumber })
    }
  }

  handleIdentityTypeInput = (e) => {
    let idType = e.target.value

    if (idType === "za_id"
      && this.state.identityValue !== null
      && isValidSouthAfricanID(this.state.identityValue, moment)
      && (!this.state.dateOfBirth || !this.state.dateOfBirth.isValid())) {

      let dob = getDateOfBirthFromSouthAfricanID(this.state.identityValue, moment)
      this.setState({
        identityType: idType,
        dateOfBirth: dob
      })
    } else {
      this.setState({ identityType: idType })
    }
  }

  handleDateOfBirthChange = (date) => {
    this.setState({
      dateOfBirth: date
    })
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className="slds-is-relative" />
    }

    if (this.props.data.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    const identity = this.props.data.identity

    let identityInfoRows = (
      <tbody>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            Name
          </th>
          <td>
            {this.state.editPersonalInfo
              ? <Input
                  id="firstName"
                  placeholder='Name'
                  value={this.state.firstName || ""}
                  onChange={(e) => this.setState({firstName: e.target.value})}
                />
              : identity.firstName
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            Surname
          </th>
          <td>
            {this.state.editPersonalInfo
              ? <Input
                  id="lastName"
                  placeholder='Surname'
                  value={this.state.lastName || ""}
                  onChange={(e) => this.setState({lastName: e.target.value})}
                />
              : identity.lastName
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            Other name
          </th>
          <td>
            {this.state.editPersonalInfo
              ? <Input
                  id="otherName"
                  placeholder='Other name'
                  value={this.state.otherName || ""}
                  onChange={(e) => this.setState({otherName: e.target.value})}
                />
              : identity.otherName
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            ID number
          </th>
          <td>
            {this.state.editPersonalInfo
              ? <Input
                  id="identityValue"
                  placeholder='Identity number'
                  value={this.state.identityValue || ""}
                  onChange={this.handleIdentityNumberInput}
                />
              : identity.identityValue
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            ID type
          </th>
          <td>
            {this.state.editPersonalInfo
              ? <SelectWithData
                  id="identityType"
                  forField="IdentityIdentityType"
                  value={this.state.identityType}
                  onChange={this.handleIdentityTypeInput}
                  hasError={this.state.identityValue && !this.state.identityType}
                />
              : identity.identityType ? <SpanWithData forField="IdentityIdentityType" searchKey={identity.identityType} /> : "not specified"
              }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            Born
          </th>
          <td>
            {this.state.editPersonalInfo
              ? <DatePicker
                  selected={this.state.dateOfBirth}
                  onChange={this.handleDateOfBirthChange}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  dateFormat="ll"
                  maxDate={moment()}
                />
              : identity.dateOfBirth
                ? moment(identity.dateOfBirth).format("ll")
                : null
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            Language
          </th>
          <td>
            {this.state.editPersonalInfo
              ? <SelectWithData
                  id="language"
                  forField="IdentityLanguage"
                  value={this.state.language}
                  onChange={(e) => this.setState({language: e.target.value})}
                />
               : <SpanWithData forField="IdentityLanguage" searchKey={identity.language} />
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            Life status
          </th>
          <td>
            {this.state.editPersonalInfo
              ? <RadioGroup
                  id='deceased'
                  options={[
                    {
                      label: "Alive",
                      value: false
                    },
                    {
                      label: "Deceased",
                      value: true
                    }
                  ]}
                  value={this.state.deceased}
                  gridLayout={true}
                  onClick={(e) => this.setState({ deceased: e.target.value === 'true' })}
                  booleanValues={true}
                />
              : identity.deceased ? "Deceased" : "Alive"
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            Membership type
          </th>
          <td>
            {identity.membershipType
              ? <div className='slds-grid'>
                  <SpanWithData forField="IdentityMembershipType" searchKey={identity.membershipType} />
                  <Tooltip
                    description="Client's membership type"  // TODO: have description be specific to membership type
                  />
                </div>
              : "Unknown"
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            Membership status
          </th>
          <td>
            {identity.membershipStatus
              ? <div className='slds-grid'>
                  <SpanWithData forField="IdentityMembershipStatus" searchKey={identity.membershipStatus} />
                  <Tooltip
                    description="Client's membership status"  // TODO: have description be specific to membership status
                  />
                </div>
              : "Unknown"
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            EarnBacks type
          </th>
          <td>
            {identity.earnbacksType
              ? <div className='slds-grid'>
                  <SpanWithData forField="IdentityEarnbacksType" searchKey={identity.earnbacksType} />
                  <Tooltip
                    description="Client's EarnBacks type"  // TODO: have description be specific to EarnBacks type
                  />
                </div>
              : "Unknown"
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            EarnBacks status
          </th>
          <td>
            {identity.earnbacksStatus
              ? <div className='slds-grid'>
                  <SpanWithData forField="IdentityEarnbacksStatus" searchKey={identity.earnbacksStatus} />
                  <Tooltip
                    description="Client's EarnBacks status"  // TODO: have description be specific to EarnBacks status
                  />
                </div>
              : "Unknown"
            }
          </td>
        </tr>
        <tr>
          <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
            Products status
          </th>
          <td>
            {identity.productsStatus
              ? <div className='slds-grid'>
                  <SpanWithData forField="IdentityProductsStatus" searchKey={identity.productsStatus} />
                  <Tooltip
                    description="Client's products status"  // TODO: have description be specific to products status
                  />
                </div>
              : "Unknown"
            }
          </td>
        </tr>
        {identity.client &&
          <tr>
            <th className={`slds-size_${this.props.keyColumnSize} slds-theme_shade slds-cell-wrap slds-text-heading_label`}>
              Employment status
            </th>
            <td>
              <SpanWithData forField='ClientStatus' searchKey={identity.client.status} />
            </td>
          </tr>
        }
      </tbody>
    )

    if (this.props.readOnly) {
      return (
        <InfoDisplay
          title={this.props.title}
          rows={identityInfoRows}
          noButtons={true}
          className='slds-m-bottom_x-large'
        />
      )
    } else {
      return (
        <InfoDisplay
          title={this.props.title}
          rows={identityInfoRows}
          edit={this.state.editPersonalInfo}
          onCancelClick={this.goReadOnly}
          onSaveClick={this.handlePersonalInfoUpdate}
          onEditClick={this.goEditPersonalInfo}
          saveDisabled={this.noChangesToSave() || this.state.saveInProgress}
          saveButtonLabel={this.state.saveInProgress ? 'Saving...' : null}
          className='slds-m-bottom_x-large'
        />
      )
    }
  }
}

const getIdentityInformation = gql`query($id: ID!) {
    identity(id: $id) {
      ...Identity
      client {
        id
        status
      }
    }
  }
  ${fragments.identity}
`

const updateIdentity = gql`
  mutation updateIdentity($input: IdentityMutationInput!) {
    identityMutate(input: $input) {
      identity {
        ...Identity
      }
    }
  }
  ${fragments.identity}
`

const withData =
  graphql(getIdentityInformation, {
    options: (ownProps) => ({
      variables: {
        id: ownProps.id
      }
    })
  })

const withIdentityMutate =
  graphql(updateIdentity, {
    name: 'updateIdentity'
  })

export default compose(
  withData,
  withIdentityMutate
)(IdentityInformation)


// TODO: extract Tooltip into reusable component
class Tooltip extends React.Component {
  static propTypes = {
    description: PropTypes.string.isRequired
  }

  constructor(props) {
    super(props)

    this.state = {
      open: false
    }
  }

  render() {
    return (
      <div style={{ position: 'relative' }}>
        <div className="slds-form-element slds-m-left_small">
          <div className="slds-form-element__icon">
            <button
              className="slds-button slds-button_icon slds-button slds-button_icon"
              aria-describedby="help"
              title="Help"
              onClick={() => this.setState({ open: true })}
              onMouseLeave={() => this.setState({ open: false })}>
              <SvgIcon
                variant='utility'
                symbol='info'
                buttonIcon={true}
              />
              <span className="slds-assistive-text">Help</span>
            </button>
          </div>
        </div>
        <div
          className={`slds-popover slds-popover_tooltip slds-nubbin_left ${this.state.open ? 'slds-rise-from-ground' : 'slds-fall-into-ground'}`}
          role="tooltip"
          id="help"
          style={{ position: 'absolute', top: '-8px',  left: '40px'}}>
          <div className="slds-popover__body">
            {this.props.description}
          </div>
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/identity-information/index.js